import { useMemo } from 'react';

import { NavigationLink } from 'components/NavigationLink';
import { lodash } from 'helpers';
import { TranslationPath, useTranslation } from 'libs/i18n';
import { useMatches, RoutesId } from 'libs/navigation/';
import { Breadcrumbs, Typography } from 'libs/ui';
import { Colors } from 'libs/ui/theme';

const breadcrumbsPages: Partial<Record<RoutesId, TranslationPath>> = {
  [RoutesId.admins]: 'breadcrumbsPages.admins',
  [RoutesId.personClients]: 'breadcrumbsPages.clients.personal',
  [RoutesId.legalClients]: 'breadcrumbsPages.clients.legal',
  [RoutesId.orders]: 'breadcrumbsPages.orders',
  [RoutesId.directories]: 'breadcrumbsPages.directories',
  [RoutesId.operations]: 'breadcrumbsPages.operations',
};

export type NavigationBreadcrumbsTitles = Partial<Record<RoutesId, string>>;

export interface NavigationBreadcrumbsProps {
  titles?: NavigationBreadcrumbsTitles;
}

/**
 * Navigation breadcrumbs component.
 */
export const NavigationBreadcrumbs = ({
  titles,
}: NavigationBreadcrumbsProps) => {
  const matches = useMatches();

  const { t } = useTranslation();

  const breadcrumbs = useMemo(() => {
    const items = lodash.compact(
      matches.map((route) => {
        const navTitle =
          titles?.[route.id] ??
          (breadcrumbsPages[route.id]
            ? t(breadcrumbsPages[route.id]!)
            : undefined);

        if (!navTitle) return undefined;

        return { pathname: route.pathname, navTitle };
      })
    );
    return items.map(({ pathname, navTitle }, index) =>
      index === items.length - 1 ? (
        <Typography variant="captionMedium">{navTitle}</Typography>
      ) : (
        <NavigationLink key={pathname} to={pathname}>
          <Typography color={Colors.gray600} variant="captionMedium">
            {navTitle}
          </Typography>
        </NavigationLink>
      )
    );
  }, [matches, t, titles]);

  return <Breadcrumbs breadcrumbs={breadcrumbs} />;
};
